import { CollectDataStepType } from 'src/constants/collect-data/enums'
import type {
  CollectDataApprovalType,
  CollectDataTemplateStepBackgroundCheckMetadata,
  CollectDataTemplateStepDotNumberMetadata,
  CollectDataTemplateStepFormMetadata,
  CollectDataTemplateStepInsuranceMetadata,
  CollectDataTemplateStepLmsTrainingMetadata,
  CollectDataTemplateStepMvrMetadata,
  CollectDataTemplateStepSignContractMetadata,
  CollectDataTemplateStepVehicleAndInsuranceMetadata,
} from 'src/interfaces/onboardingTemplate'

// ─── Mirrors Nest `CollectDataTemplateUpdateDto` (PUT …/collect-data-templates/:id) ───

export enum CollectDataTemplateStepsConfigEvents {
  Created = 'config_created',
  Updated = 'config_updated',
  Deleted = 'config_deleted',
}

export enum CollectDataTemplateStepEvents {
  Created = 'step_created',
  Updated = 'step_updated',
  Deleted = 'step_deleted',
}

/** Shared optional step fields — `CreateCollectDataTemplateStepDto` / `UpdateCollectDataTemplateStepDto`. */
export interface CollectDataTemplateStepMutationFieldsDto {
  optional?: boolean
  order?: number
  /** Nest `@Min(1)` when present */
  expiresInDays?: number
  requireExpirationDate?: boolean
  /** Nest `@Min(0)` when present */
  maxAttempts?: number
  label?: string
  approvalType?: CollectDataApprovalType
  ignoreMonitoring?: boolean
  /** BGC / MVR only (`ValidateIf` on Nest) */
  continueOnIncomplete?: boolean
  recollectSameTypeSteps?: boolean
  skipRetroactiveCollectIfAnySameTypeCompleted?: boolean
  retroactiveCollectRequired?: boolean
}

/** Metadata allowed on `step_created` (`CreateCollectDataTemplateStepDto`). */
export type CollectDataTemplateStepCreateMetadataDto =
  | CollectDataTemplateStepSignContractMetadata
  | CollectDataTemplateStepFormMetadata
  | CollectDataTemplateStepBackgroundCheckMetadata
  | CollectDataTemplateStepMvrMetadata
  | CollectDataTemplateStepInsuranceMetadata
  | CollectDataTemplateStepLmsTrainingMetadata
  | CollectDataTemplateStepVehicleAndInsuranceMetadata
  | CollectDataTemplateStepDotNumberMetadata

/**
 * Metadata allowed on `step_updated` (`UpdateCollectDataTemplateStepDto` / `CollectDataTemplateUpdateStepPayloadDto`).
 * Nest `ValidateIf` omits SSN (`pii_collection`) on update actions.
 */
export type CollectDataTemplateStepUpdateMetadataDto =
  | CollectDataTemplateStepSignContractMetadata
  | CollectDataTemplateStepFormMetadata
  | CollectDataTemplateStepBackgroundCheckMetadata
  | CollectDataTemplateStepMvrMetadata
  | CollectDataTemplateStepInsuranceMetadata
  | CollectDataTemplateStepLmsTrainingMetadata
  | CollectDataTemplateStepVehicleAndInsuranceMetadata
  | CollectDataTemplateStepDotNumberMetadata

/** `CreateCollectDataTemplateStepDto` — nested under `step_created`. */
export interface CollectDataTemplateCreateStepPayloadDto
  extends CollectDataTemplateStepMutationFieldsDto {
  type: CollectDataStepType
  metadata?: CollectDataTemplateStepCreateMetadataDto
}

/**
 * `CollectDataTemplateUpdateStepPayloadDto` — `UpdateCollectDataTemplateStepDto` + `id`.
 * All fields except `id` are optional; `type` is optional on the update payload class.
 */
export interface CollectDataTemplateUpdateStepPayloadDto
  extends CollectDataTemplateStepMutationFieldsDto {
  id: string
  type?: CollectDataStepType
  metadata?: CollectDataTemplateStepUpdateMetadataDto
}

export interface CollectDataTemplateDeleteStepPayloadDto {
  id: string
}

export interface CollectDataTemplateDeleteConfigPayloadDto {
  id: string
}

/** Nested under `config_created`; discriminator allows only `step_created` on Nest. */
export type CollectDataTemplateCreateStepActionDto = {
  type: CollectDataTemplateStepEvents.Created
  payload: CollectDataTemplateCreateStepPayloadDto
}

export type CollectDataTemplateUpdateStepActionDto = {
  type: CollectDataTemplateStepEvents.Updated
  payload: CollectDataTemplateUpdateStepPayloadDto
}

export type CollectDataTemplateDeleteStepActionDto = {
  type: CollectDataTemplateStepEvents.Deleted
  payload: CollectDataTemplateDeleteStepPayloadDto
}

export type CollectDataTemplateStepActionDto =
  | CollectDataTemplateCreateStepActionDto
  | CollectDataTemplateUpdateStepActionDto
  | CollectDataTemplateDeleteStepActionDto

/** `CollectDataTemplateCreateConfigPayloadDto` — no config `id` (server assigns). */
export interface CollectDataTemplateCreateConfigPayloadDto {
  label: string
  delayed?: boolean
  order?: number
  title?: string
  description?: string
  insurancePolicyId?: string
  stepActions: CollectDataTemplateCreateStepActionDto[]
}

/**
 * `CollectDataTemplateUpdateConfigPayloadDto`.
 * Note: Nest does not include `insurancePolicyId` on config update actions — only on create.
 */
export interface CollectDataTemplateUpdateConfigPayloadDto {
  id: string
  label?: string
  delayed?: boolean
  order?: number
  title?: string
  description?: string
  stepActions?: CollectDataTemplateStepActionDto[]
}

export type CollectDataTemplateCreateConfigActionDto = {
  type: CollectDataTemplateStepsConfigEvents.Created
  payload: CollectDataTemplateCreateConfigPayloadDto
}

export type CollectDataTemplateUpdateConfigActionDto = {
  type: CollectDataTemplateStepsConfigEvents.Updated
  payload: CollectDataTemplateUpdateConfigPayloadDto
}

export type CollectDataTemplateDeleteConfigActionDto = {
  type: CollectDataTemplateStepsConfigEvents.Deleted
  payload: CollectDataTemplateDeleteConfigPayloadDto
}

export type CollectDataTemplateActionDto =
  | CollectDataTemplateCreateConfigActionDto
  | CollectDataTemplateUpdateConfigActionDto
  | CollectDataTemplateDeleteConfigActionDto

export interface CollectDataTemplateUpdateDto {
  actions: CollectDataTemplateActionDto[]
}

/**
 * Mirrors Nest `AdminCollectDataTemplateCreateV2Dto` (POST …/collect-data-templates).
 * `actions` is `config_created` **only** — the create endpoint does not accept the full update
 * union, and rejects unknown properties outright (`forbidNonWhitelisted: true`).
 */
export interface AdminCollectDataTemplateCreateDto {
  partnerId: string
  name: string
  actions: CollectDataTemplateCreateConfigActionDto[]
}
